import { artistHref } from '@/features/artists/artistHref'
import { ListMusic, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { usePlayer, type QueueItem } from '@/audio/store'
import { TrackRow } from '@/components/TrackRow'
import { Button, EmptyState } from '@/components/ui'

import { QueueHistory } from './QueueHistory'
import { QueueUpNext } from './QueueUpNext'

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-text-muted">
      {children}
    </h3>
  )
}

interface QueueRowProps {
  item: QueueItem
  isActive: boolean
  isPlaying: boolean
  onPlay: () => void
  onRemove: () => void
}

function QueueRow({ item, isActive, isPlaying, onPlay, onRemove }: QueueRowProps) {
  return (
    <li>
      <TrackRow
        title={item.title}
        artistName={item.artistName}
        artistHref={artistHref({ local_artist_id: item.localArtistId, artist_id: item.artistId })}
        albumName={item.albumName}
        durationMs={item.durationMs}
        coverUrl={item.coverUrl}
        isActive={isActive}
        isPlaying={isPlaying}
        onPlay={onPlay}
        includeArtistMenu={false}
        menuItems={[{ label: 'Remove from queue', icon: Trash2, destructive: true, onSelect: onRemove }]}
      />
    </li>
  )
}

/** Shared queue presentation for the sheet and the expanded player. */
export function QueueContents() {
  const queue = usePlayer((state) => state.queue)
  const index = usePlayer((state) => state.index)
  const isPlaying = usePlayer((state) => state.isPlaying)
  const playAt = usePlayer((state) => state.playAt)
  const removeAt = usePlayer((state) => state.removeAt)
  const activeIndex = index >= 0 && index < queue.length ? index : -1

  const rowFor = (item: QueueItem, position: number) => (
    <QueueRow
      key={item.uid}
      item={item}
      isActive={position === activeIndex}
      isPlaying={isPlaying}
      onPlay={() => playAt(position)}
      onRemove={() => removeAt(position)}
    />
  )

  return queue.length === 0 ? (
    <EmptyState
      icon={ListMusic}
      title="The queue is empty"
      description="Play a song from your library and it will appear here."
      className="py-10"
    />
  ) : (
    <>
      {activeIndex >= 0 ? (
        <>
          <SectionHeading>{queue[activeIndex].suggested ? 'Now playing · Similar song' : 'Now playing'}</SectionHeading>
          <ul>{rowFor(queue[activeIndex], activeIndex)}</ul>
        </>
      ) : null}

      <SectionHeading>Up next</SectionHeading>
      <QueueUpNext offset={activeIndex + 1} />
      <QueueHistory />
    </>
  )
}

export function QueueClearButton({ onBeforeClear }: { onBeforeClear: () => void }) {
  const count = usePlayer((state) => state.queue.length)
  const clearQueue = usePlayer((state) => state.clearQueue)
  if (!count) return null
  return <Button variant="ghost" size="sm" className="text-danger" onClick={() => {
    // Clearing unmounts the player, so close its overlay first.
    onBeforeClear()
    clearQueue()
  }}>Clear</Button>
}
